import classNames from 'classnames';
import { CSSProperties, ReactNode } from 'react';
import { FormattedNumber, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Clock } from 'atoms/icons/Clock';
import { Live } from 'atoms/icons/Live';
import { SquadCaptain } from 'atoms/icons/SquadCaptain';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { LabelL, LabelM, LabelS } from 'atoms/typography';
import { Ellipsis } from 'atoms/typography/Ellipsis';
import { RivalsLightning } from 'components/rivals/RepScore/RivalsLightning';
import { gameStatusMessages } from 'lib/glossary';
import { tabletAndAbove } from 'style/mediaQuery';

import { CumulatedScoreDifference } from '../../CumulatedScoreDifference';
import { PodiumIcon } from '../../PodiumIcon';

export const Row = styled.tr`
  height: var(--table-row-height, calc(8 * var(--unit)));
  &:hover {
    background: rgba(var(--c-rgb-white), 0.11);
    cursor: pointer;
  }
  &.selected {
    background: rgba(var(--c-rgb-white), 0.22);
  }
  &.current {
    background: linear-gradient(
        rgba(var(--c-rgb-white), 0.11),
        rgba(var(--c-rgb-white), 0.11)
      ),
      var(--current-bg-color);
    position: sticky;
    z-index: 1;
    top: calc(var(--table-header-height, 0) + var(--current-stack-height));
    bottom: var(--bottom-bar-height-mobile);

    @media ${tabletAndAbove} {
      bottom: 0;
    }
  }
  &.current:hover,
  .current.selected {
    background: linear-gradient(
        rgba(var(--c-rgb-white), 0.22),
        rgba(var(--c-rgb-white), 0.22)
      ),
      var(--current-bg-color);
  }
  & > td {
    padding-left: var(--intermediate-unit);
  }
`;
const PointsText = styled(Horizontal)`
  gap: 2px;
  padding: 0 var(--unit) 0 0;
`;
const AvatarPadding = styled.div`
  padding: var(--intermediate-unit) 0;
`;
const GrowingTableDivision = styled.td`
  width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
`;

const UserRowWithReward = styled(Horizontal)`
  align-items: center;
  justify-content: space-between;
  padding-right: var(--double-unit);
`;
const LiveGames = styled(Horizontal).attrs({ gap: 0.5 })`
  background-color: var(--c-rivals-arena);
  color: var(--c-black);
  border-radius: 100px;
  padding: 0 var(--half-unit);
`;

const UpcomingGames = styled(Horizontal).attrs({ gap: 0.5 })`
  background-color: var(--c-score-high);
  color: var(--c-black);
  border-radius: 100px;
  padding: 0 var(--half-unit);
`;

const UsersAndGames = styled(Vertical).attrs({ gap: 0.5 })``;

export type DivisionRanking = {
  id: string;
  captain?: boolean;
  manager: { id: string; user: { slug: string; nickname: string } };
  points: number;
  ranking: number;
  cumulatedScoreDifference: number;
  liveGamesCount: number;
  upcomingGamesCount: number;
  eligibleReward: {
    probabilisticBundleConfiguration: {
      id: string;
      pictureUrl: string;
    } | null;
  } | null;
};

type Props = {
  divisionRanking: DivisionRanking;
  featuredManagerId?: string;
  selected?: boolean;
  promoted?: boolean;
  relegated?: boolean;
  onRowClick?: (userSlug: string) => void;
  avatar: ReactNode;
};
export const SeasonLearderboardRow = ({
  divisionRanking,
  featuredManagerId,
  selected,
  promoted,
  relegated,
  avatar,
  onRowClick,
}: Props) => {
  const {
    manager,
    captain,
    points,
    ranking,
    cumulatedScoreDifference,
    liveGamesCount,
    upcomingGamesCount,
    eligibleReward,
  } = divisionRanking;
  const rewardPictureUrl =
    eligibleReward?.probabilisticBundleConfiguration?.pictureUrl;

  let currentBgColor = 'var(--c-black)';

  if (promoted) {
    currentBgColor = 'var(--promoted-color)';
  }
  if (relegated) {
    currentBgColor = 'var(--relegated-color)';
  }

  const { formatMessage } = useIntl();

  return (
    <Row
      onClick={onRowClick ? () => onRowClick(manager.user.slug) : undefined}
      tabIndex={0}
      className={classNames({
        current: featuredManagerId === divisionRanking.manager.id,
        selected,
      })}
      style={{ '--current-bg-color': currentBgColor } as CSSProperties}
    >
      <td>
        <Horizontal gap={1.5}>
          <PodiumIcon rank={ranking} />
        </Horizontal>
      </td>
      <GrowingTableDivision>
        <UserRowWithReward>
          <Horizontal gap={1.5}>
            <AvatarPadding>{avatar}</AvatarPadding>
            <UsersAndGames>
              <Horizontal gap={0} center>
                <Ellipsis>
                  <LabelM as="span">{manager.user.nickname}</LabelM>
                </Ellipsis>
                {captain && <SquadCaptain />}
              </Horizontal>
              <Horizontal gap={0.5}>
                {liveGamesCount > 0 && (
                  <LiveGames>
                    <Live
                      title={formatMessage(gameStatusMessages.live)}
                      width={12}
                      height={12}
                    />
                    <LabelS as="span">{liveGamesCount}</LabelS>
                  </LiveGames>
                )}
                {upcomingGamesCount > 0 && (
                  <UpcomingGames>
                    <Clock
                      title={formatMessage(gameStatusMessages.scheduled)}
                      width={8}
                      height={8}
                    />
                    <LabelS as="span">{upcomingGamesCount}</LabelS>
                  </UpcomingGames>
                )}
              </Horizontal>
            </UsersAndGames>
          </Horizontal>
          {rewardPictureUrl && (
            <img src={rewardPictureUrl} alt="" width={20} height={20} />
          )}
        </UserRowWithReward>
      </GrowingTableDivision>
      <td>
        <CumulatedScoreDifference
          cumulatedScoreDifference={cumulatedScoreDifference}
        />
      </td>
      <td>
        <PointsText>
          <LabelL bold>
            <FormattedNumber value={points} />
          </LabelL>
          <RivalsLightning fill="var(--c-rivals-reputation)" />
        </PointsText>
      </td>
    </Row>
  );
};
